/**
 * FENHistoryManager - Centralized FEN History Management
 *
 * Phase 4 - SOC Refactoring
 *
 * Single source of truth for all FEN history operations.
 * Consolidates FEN history logic that was previously scattered across ZodiacEngine,
 * MovementManager, and various mode-switching methods.
 *
 * RESPONSIBILITIES:
 * - Track FEN history throughout the game
 * - Initialize FEN history when loading positions
 * - Add FEN entries with deduplication
 * - Detect threefold repetition
 * - Clear/reset FEN history
 * - Generate current FEN from game state
 */

import { GameState } from '../types';
import { ZodiacFEN } from '../Fen/ZodiacFEN';

export class FENHistoryManager {
  /**
   * Add a FEN string to the history
   * Deduplicates consecutive identical FENs to prevent false threefold repetition
   */
  addFEN(gameState: GameState, fen: string): void {
    const history = gameState.fenHistory;

    // Skip if this is a consecutive duplicate (compare stripped positions to catch
    // entries that differ only by metadata like @turnCount, preventing false 3-fold draws)
    if (history.length > 0) {
      const cleanNew = this.stripFENMetadata(fen);
      const cleanLast = this.stripFENMetadata(history[history.length - 1]);
      if (cleanNew === cleanLast) return;
    }

    // Create new array to ensure React detects change
    gameState.fenHistory = [...history, fen];
  }

  /**
   * Add multiple FENs to the history (from a turn's worth of moves)
   * Deduplicates consecutive identical FENs
   */
  addFENs(gameState: GameState, fens: string[]): void {
    let newHistory = [...gameState.fenHistory];

    fens.forEach((fen) => {
      // Compare stripped positions to catch entries that differ only by metadata
      // (e.g. @turnCount, d prefix). Prevents Virus/bonus turn multi-step moves
      // from creating false 3-fold repetition draws.
      const cleanNew = this.stripFENMetadata(fen);
      const cleanLast = newHistory.length > 0
        ? this.stripFENMetadata(newHistory[newHistory.length - 1])
        : '';

      if (cleanNew !== cleanLast) {
        newHistory.push(fen);
      }
    });

    // Create new array to ensure React detects change
    gameState.fenHistory = newHistory;
  }

  /**
   * Get the full FEN history
   */
  getFENHistory(gameState: GameState): string[] {
    return gameState.fenHistory;
  }

  /**
   * Clear the FEN history
   */
  clearHistory(gameState: GameState): void {
    gameState.fenHistory = [];
  }

  /**
   * Initialize FEN history with a starting position
   * Used when loading from FEN or starting a new game mode
   */
  initializeWithFEN(gameState: GameState, fen: string): void {
    gameState.fenHistory = [fen];
  }

  /**
   * Initialize FEN history with current game state
   * Used when switching modes (TestAI, Watch, etc.)
   */
  initializeWithCurrentState(gameState: GameState): void {
    const currentFEN = ZodiacFEN.encodeWithoutKings(gameState);
    this.initializeWithFEN(gameState, currentFEN);
  }

  /**
   * Check if the current position has occurred 3+ times (threefold repetition)
   * This is a draw condition in chess-like games
   */
  hasThreefoldRepetition(gameState: GameState): boolean {
    const currentFEN = ZodiacFEN.encodeWithoutKings(gameState);
    const count = this.getRepetitionCount(gameState, currentFEN);
    return count >= 3;
  }

  /**
   * Count how many times a specific FEN has occurred in the history
   * Strips out metadata annotations (@turnCount, ~{placement}, #dice, turn indicators)
   * to compare only the actual board position
   */
  getRepetitionCount(gameState: GameState, fen: string): number {
    // Strip metadata from the target FEN
    const cleanTargetFEN = this.stripFENMetadata(fen);

    // Count how many times this position appears in history
    let count = 0;
    gameState.fenHistory.forEach((historicFEN) => {
      const cleanHistoricFEN = this.stripFENMetadata(historicFEN);
      if (cleanHistoricFEN === cleanTargetFEN) {
        count++;
      }
    });

    return count;
  }

  /**
   * Strip all metadata from a FEN string to get just the board position
   * Removes: @turnCount, ~{placement:...}, ~{moved:...}, #dice, turn indicator prefix (d)
   */
  private stripFENMetadata(fen: string): string {
    let clean = fen;

    // Remove turn indicator prefix (d at start)
    if (clean.startsWith('d')) {
      clean = clean.substring(1);
    }

    // Remove @turnCount metadata
    const atIndex = clean.indexOf('@');
    if (atIndex !== -1) {
      clean = clean.substring(0, atIndex);
    }

    // Remove ~{placement:...} annotations
    const tildeIndex = clean.indexOf('~');
    if (tildeIndex !== -1) {
      clean = clean.substring(0, tildeIndex);
    }

    // Remove #dice annotations
    const hashIndex = clean.indexOf('#');
    if (hashIndex !== -1) {
      clean = clean.substring(0, hashIndex);
    }

    return clean;
  }

  /**
   * Get the current FEN representation of the game state
   */
  getCurrentFEN(gameState: GameState): string {
    return ZodiacFEN.encodeWithoutKings(gameState);
  }

  /**
   * Get the last FEN in the history
   */
  getLastFEN(gameState: GameState): string | null {
    const history = gameState.fenHistory;
    return history.length > 0 ? history[history.length - 1] : null;
  }

  /**
   * Get the history length
   */
  getHistoryLength(gameState: GameState): number {
    return gameState.fenHistory.length;
  }

  /**
   * Add an annotated FEN (used for sandbox placements, tutorial moves, etc.)
   * Format: "FEN~{metadata}"
   */
  addAnnotatedFEN(gameState: GameState, fen: string, annotation: string): void {
    const annotatedFEN = `${fen}~{${annotation}}`;
    this.addFEN(gameState, annotatedFEN);
  }

  /**
   * Replace the most recent FEN in history
   * Used in special cases where we need to correct the last entry
   */
  replaceLastFEN(gameState: GameState, newFEN: string): void {
    if (gameState.fenHistory.length === 0) {
      return;
    }

    const newHistory = [...gameState.fenHistory];
    newHistory[newHistory.length - 1] = newFEN;
    gameState.fenHistory = newHistory;
  }
}
